import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useTheme } from "../context/ThemeContext";
import { useReminders } from "../hooks/useReminders";
import { Button } from "./ui/button";
import FloatingAssistant from "./FloatingAssistant";
import { HeartPulse, LayoutDashboard, Apple, Calculator, History as HistoryIcon, Settings as SettingsIcon, Bot, Shield, LogOut, Moon, Sun, Languages } from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const { theme, toggle } = useTheme();
  const loc = useLocation();
  const nav = useNavigate();
  const guestMode =
    typeof window !== "undefined" &&
    localStorage.getItem("guestMode") === "true";
  useReminders();

  const clearGuestSessionData = () => {
    localStorage.removeItem("guestMode");
    localStorage.removeItem("guestProfile");
    localStorage.removeItem("guestFoodLogs");
    localStorage.removeItem("ddp-medications-guest");
    localStorage.removeItem("ddp-medication-history-guest");
    localStorage.removeItem("ddp-meal-reminders-guest");
    localStorage.removeItem("ddp-medication-notifications-guest");
    localStorage.removeItem("ddp-medication-notifications-fired-guest");
    localStorage.removeItem("ddp-meal-mixer-guest");
    localStorage.removeItem("ddp-ai-meal-draft-guest");
    localStorage.removeItem("ddp-saved-mixed-meals-guest");
    localStorage.removeItem("ddp-medications-undefined");
    localStorage.removeItem("ddp-medication-history-undefined");
  };

  const handleLogout = () => {
    if (guestMode) {
      clearGuestSessionData();
      nav("/login");
      return;
    }

    if (user?.id) {
      localStorage.removeItem(`ddp-meal-mixer-${user.id}`);
    }

    logout();
    nav("/");
  };

  const items = [
    { to: "/dashboard", label: t("nav_dashboard"), icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/foods", label: t("nav_foods"), icon: Apple, testid: "nav-foods" },
    { to: "/meal-mixer", label: t("nav_meal_mixer"), icon: Calculator, testid: "nav-meal-mixer" },
    { to: "/history", label: t("nav_history"), icon: HistoryIcon, testid: "nav-history" },
    { to: "/assistant", label: t("nav_assistant"), icon: Bot, testid: "nav-assistant" },
    { to: "/settings", label: t("nav_settings"), icon: SettingsIcon, testid: "nav-settings" },
  ];
  if (user?.role === "admin") items.push({ to: "/admin", label: t("nav_admin"), icon: Shield, testid: "nav-admin" });

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-64 lg:min-h-screen border-b lg:border-b-0 lg:border-e border-border bg-card flex-shrink-0">
        <Link to={user ? "/dashboard" : "/"} className="p-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "var(--rs-primary)" }}>
            <HeartPulse className="w-6 h-6 text-white" strokeWidth={2} />
          </div>
          <div>
            <div className="font-extrabold text-lg" style={{ fontFamily: "Manrope" }}>{t("brand")}</div>
            <div className="text-xs text-muted-foreground">{user?.name}</div>
          </div>
        </Link>
        <nav className="px-3 pb-4 space-y-1">
          {items.map((it) => {
            const active = loc.pathname === it.to;
            const Icon = it.icon;
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-base transition-all ${
                  active ? "bg-primary text-primary-foreground font-semibold" : "hover:bg-muted text-foreground"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{it.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border space-y-2">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={toggle}
              className="flex-1 gap-2"
              data-testid="theme-toggle"
            >
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              {theme === "dark" ? t("light") : t("dark")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLang(lang === "en" ? "ar" : "en")}
              className="flex-1 gap-2"
              data-testid="lang-toggle"
            >
              <Languages className="w-4 h-4" />
              {lang === "en" ? "AR" : "EN"}
            </Button>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            {t("logout")}
          </Button>
          <nav aria-label="Legal pages" className="border-t border-border pt-3 flex flex-wrap gap-x-3 gap-y-2 text-xs text-muted-foreground">
            <Link to="/terms" className="hover:text-primary">
              {lang === "ar" ? "الشروط" : "Terms"}
            </Link>
            <Link to="/privacy" className="hover:text-primary">
              {lang === "ar" ? "الخصوصية" : "Privacy"}
            </Link>
            <Link to="/medical-disclaimer" className="hover:text-primary">
              {lang === "ar" ? "إخلاء المسؤولية" : "Disclaimer"}
            </Link>
          </nav>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-8 overflow-x-hidden">{children}</main>
      {loc.pathname !== "/assistant" && <FloatingAssistant />}
    </div>
  );
}
